/**
 * Parses a delivery date range from a comma-separated delivery-time string.
 *
 * The enhanced EDD range endpoints are not reliable for all brands and regions. Callers must
 * check `tempShowEddDateRange` before calling this composable. See {@link useFormattedDeliveryDate}
 * for the common case. `Search.vue` and `ShippingMethod.vue` call this directly when they need
 * the raw range value.
 * @param deliveryTime - Raw delivery time value returned by the API.
 * @param options - Optional `Intl.DateTimeFormatOptions` applied to both dates.
 * @returns `startDate - endDate` string when both values parse as valid dates, or `undefined`
 * when the value cannot be parsed as a two-date range.
 * @category Composables
 */
export const useDeliveryDateRange = (
  deliveryTime: string,
  options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    weekday: 'short',
  }
): string | undefined => {
  const deliveryTimeValues = deliveryTime
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)

  if (deliveryTimeValues.length !== 2)
    return

  const parseLocalDate = (value: string): Date => {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number)

    return new Date(year, month - 1, day)
  }

  const [startDate, endDate] = deliveryTimeValues

  const start = parseLocalDate(startDate)
  const end = parseLocalDate(endDate)

  if ([start, end].some((date) => Number.isNaN(date.getTime())))
    return

  const formattedStartDate = useDeliveryTime(start, options)
  const formattedEndDate = useDeliveryTime(end, options)

  return formattedStartDate === formattedEndDate ? formattedStartDate : `${formattedStartDate} - ${formattedEndDate}`
}
